import {IconButton, InfoTooltip} from '@wandb/components';
import {Button} from '@wandb/weave/components/Button';
import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {useRampFlagMarimoPanelsUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useQuickAddKeys} from '../PanelsQuickAdd/hooks/useQuickAddKeys';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {AddPanelsMenuGroup} from './AddPanelsMenuGroup';
import {
  ADD_PANELS_MENU_GROUPS,
  ADD_PANELS_MENU_GROUPS_WITH_MARIMO,
  ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD,
  ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD_AND_MARIMO,
} from './constants';
import {useDefaultSectionRefs} from './hooks/useDefaultSectionRefs';
import {
  isPanelTypeAlwaysEnabled,
  useIsPanelTypeEnabled,
} from './hooks/useIsPanelTypeEnabled';
import {type AddPanelsMenuGroupProps} from './types';

export const AddPanels = () => {
  const {closeWorkspaceDrawer, targetSectionRef: drawerSectionRef} =
    useWorkspaceDrawerContext();
  const {checkPanelEnabled, isLoading, isError, retry} =
    useIsPanelTypeEnabled();
  const isMarimoPanelsUser = useRampFlagMarimoPanelsUser();

  const {keysWithNoPanels} = useQuickAddKeys();

  const isMultiMetricQuickAddAllowed = !drawerSectionRef; // MMQA is not supported at section-level
  const isQuickAddShown =
    isMultiMetricQuickAddAllowed || keysWithNoPanels.length > 0;

  const menuGroups = useMemo(() => {
    if (isMarimoPanelsUser) {
      if (isQuickAddShown) {
        return ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD_AND_MARIMO;
      }
      return ADD_PANELS_MENU_GROUPS_WITH_MARIMO;
    }
    return isQuickAddShown
      ? ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD
      : ADD_PANELS_MENU_GROUPS;
  }, [isQuickAddShown, isMarimoPanelsUser]);

  const groupedItems: AddPanelsMenuGroupProps[] = useMemo(() => {
    return menuGroups
      .filter(group => group.items.length > 0)
      .map(group => {
        return {
          ...group,
          items: group.items.map(item =>
            isPanelTypeAlwaysEnabled(item.type)
              ? item
              : {
                  ...item,
                  isDisabled: !checkPanelEnabled(item.type),
                  isLoading: isLoading,
                }
          ),
        };
      });
  }, [menuGroups, checkPanelEnabled, isLoading]);

  // perf: calling these hooks here instead of for each AddPanelsMenuGroup/AddPanelsMenuItem
  // because they could be expensive for workspaces with lots of sections/panels
  const defaultSectionRefs = useDefaultSectionRefs(menuGroups);

  return (
    <>
      <div className="flex items-center justify-between border-b border-solid border-wb-border px-24 py-12">
        <h2 className="text-xl font-semibold leading-[40px]">Add panels</h2>

        <div
          className={twMerge('flex items-center gap-4', isLoading && 'gap-8')}>
          {isError && (
            <>
              <InfoTooltip
                id="add-panels-availability-error"
                aria-label="Couldn’t check panel availability"
                placement="bottom"
                variant="warning"
                size="md"
                mr={2}>
                Couldn’t check which panels are available. Some options may be
                missing.
              </InfoTooltip>

              <IconButton
                icon="reload-refresh"
                variant="ghost"
                size="md"
                aria-label="Retry availability check"
                tooltip={{
                  children: 'Retry availability check',
                  placement: 'bottom',
                }}
                onPress={retry}
              />
            </>
          )}
          <Button
            variant="ghost"
            icon="close"
            onClick={() => closeWorkspaceDrawer()}
          />
        </div>
      </div>

      <div className="overflow-auto" role="menu">
        {groupedItems.map((group, idx) => {
          return (
            <React.Fragment key={`add-panels-group-${group.title ?? idx}`}>
              {idx !== 0 && (
                <div role="separator" className="h-px bg-wb-border" />
              )}
              <AddPanelsMenuGroup
                {...group}
                sectionRef={
                  drawerSectionRef ??
                  (group.title
                    ? defaultSectionRefs?.get(group.title)
                    : undefined)
                }
                items={group.items}
              />
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};
